import {
  ButtonDS,
  DeleteOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  Switch,
  TextCapitalized,
  triggerToastDS,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  companyFollowUpAsync,
  companyFollowUpSlice,
  createCompanyExternalIbanAsync,
  deleteCompanyAsync,
  selectCompanyFollowUp,
  selectCompanyFollowUpStatus,
  selectCreateCompanyAcronymStatus,
  selectCreateCompanyExternalIbanStatus,
  selectDeleteCompanyStatus,
  selectUpdateCompanyAnalyticsStatus,
  selectUpdateCompanyHasGacStatus,
  selectUpdateCompanyAutoApproveDriverAccessRequestsStatus,
  selectUpdateCompanyHasGeolocationStatus,
  updateCompanyAnalyticsAsync,
  updateCompanyHasGacAsync,
  updateCompanyAutoApproveDriverAccessRequestsAsync,
  updateCompanyHasGeolocationAsync,
} from "./followUpSlice";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { CompanyFollowUp, CompanyFollowUpDisplayed } from "./followUpApi";
import { useTranslation } from "react-i18next";
import {
  ExternalIbanModal,
  ExternalIbanModalState,
} from "../modals/externalIbanModal";
import { ExternalIbanInput } from "./followUpInputs/externalIbanInput";
import { AcronymInput } from "./followUpInputs/acronymInput";
import "../../i18n/i18n";
import { selectAdminUser } from "../homeSlice";
import {
  DeleteCompanyModal,
  DeleteCompanyModalState,
} from "./modals/deleteCompanyModal";

const CONTRACTS_DRIVE_FOLDER_URL =
  "https://drive.google.com/drive/folders/1Wj5sCmlpSNsbDAQQfYvRcYWenF-o14dz";

export const CompanyFollowUpTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const companyFollowUpList = useAppSelector(selectCompanyFollowUp);
  const companyFollowUpListStatus = useAppSelector(selectCompanyFollowUpStatus);
  const createCompanyAcronymStatus = useAppSelector(
    selectCreateCompanyAcronymStatus,
  );
  const createCompanyExternalIbanStatus = useAppSelector(
    selectCreateCompanyExternalIbanStatus,
  );
  const deleteCompanyStatus = useAppSelector(selectDeleteCompanyStatus);

  const [listDisplayed, setListDisplayed] = useState<
    CompanyFollowUpDisplayed[] | null
  >(null);
  const adminUser = useAppSelector(selectAdminUser);
  const updateCompanyHasGacStatus = useAppSelector(
    selectUpdateCompanyHasGacStatus,
  );
  const updateCompanyAutoApproveDriverAccessRequestsStatus = useAppSelector(
    selectUpdateCompanyAutoApproveDriverAccessRequestsStatus,
  );
  const updateCompanyHasGeolocationStatus = useAppSelector(
    selectUpdateCompanyHasGeolocationStatus,
  );
  const updateCompanyAnalyticsStatus = useAppSelector(
    selectUpdateCompanyAnalyticsStatus,
  );

  const [modalState, setModalState] = useState<ExternalIbanModalState>({
    isVisible: false,
  });
  const [deleteCompanyModalState, setDeleteCompanyModalState] =
    useState<DeleteCompanyModalState>({
      isVisible: false,
    });

  const handleModalVisibility = useCallback(
    (visible: boolean, iban?: string, companyUuid?: string, name?: string) => {
      setModalState({
        isVisible: visible,
        ...(visible ? { iban, companyUuid, name } : {}),
      });
    },
    [],
  );

  const handleDeleteCompanyModalVisibility = useCallback(
    (visible: boolean, companyUuid?: string, name?: string) => {
      setDeleteCompanyModalState({
        isVisible: visible,
        ...(visible ? { companyUuid, name } : {}),
      });
    },
    [],
  );

  const handleConfirmDeleteCompany = useCallback(() => {
    setDeleteCompanyModalState({
      isVisible: false,
    });
    if (deleteCompanyModalState.companyUuid && adminUser?.email) {
      void dispatch(
        deleteCompanyAsync({
          companyUuid: deleteCompanyModalState.companyUuid,
          deleteBy: adminUser.email,
        }),
      );
    }
  }, [adminUser?.email, deleteCompanyModalState, dispatch]);

  const handleConfirmExternalIban = useCallback(() => {
    setModalState({
      isVisible: false,
    });
    if (modalState.companyUuid && modalState.iban) {
      void dispatch(
        createCompanyExternalIbanAsync({
          companyUuid: modalState.companyUuid,
          externalIban: modalState.iban,
        }),
      );
    }
  }, [modalState, dispatch]);

  const handleUpdateAutoApproveDriverAccessRequests = useCallback(
    (companyUuid: string, autoApproveDriverAccessRequests: boolean) => {
      if (!adminUser?.email) return;
      void dispatch(
        updateCompanyAutoApproveDriverAccessRequestsAsync({
          companyUuid,
          autoApproveDriverAccessRequests,
          updatedBy: adminUser.email,
        }),
      );
    },
    [adminUser?.email, dispatch],
  );

  const authorizedAdmin = useMemo(() => {
    return [
      "p.chopin@qivia.fr",
      "m.gatinois@qivia.fr",
      "m.banjac@qivia.fr",
      "a.chagra@qivia.fr",
    ].includes(adminUser?.email ?? "");
  }, [adminUser]);

  const pageName = "companyFollowUp";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      amount: "avg",
      merchantName: "size",
      category: "size",
      driverName: "size",
    },
  };

  const findKeysToTranslate = useCallback(
    (key: keyof CompanyFollowUp, value: string) => {
      switch (key) {
        case "status":
          return t(`companyFollowUp.status.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["status"] as (keyof CompanyFollowUpDisplayed)[],
    [],
  );

  const companyListWithId = useMemo(() => {
    const rowsWithId = companyFollowUpList.map((cd) => {
      let newDict: CompanyFollowUp & { id: string } = {
        ...cd,
        id: cd.companyUuid,
      };
      Object.entries(cd).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof CompanyFollowUpDisplayed)) {
          newDict = {
            ...newDict,
            [key]: findKeysToTranslate(
              key as keyof CompanyFollowUp,
              value as string,
            ),
          };
        }
      });
      return newDict;
    });
    return rowsWithId;
  }, [companyFollowUpList, findKeysToTranslate, keysToTranslate]);

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const searchBarProps = {
    values: companyListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const isAcronymValid = useCallback(
    (input: string) =>
      companyFollowUpList.reduce(
        (acc, curr) => acc && curr.acronym !== input,
        true,
      ),
    [companyFollowUpList],
  );

  useEffect(() => {
    if (createCompanyAcronymStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.acronym.create.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (createCompanyAcronymStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.acronym.create.failure") || "",
        "ERROR",
      );
    }
  }, [createCompanyAcronymStatus, t, dispatch]);

  useEffect(() => {
    if (createCompanyExternalIbanStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.externalIban.create.success") || "",
        "SUCCESS",
      );
      dispatch(
        companyFollowUpSlice.actions.resetCreateCompanyExternalIbanStatus(),
      );
    } else if (createCompanyExternalIbanStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.externalIban.create.failure") || "",
        "ERROR",
      );
    }
  }, [createCompanyExternalIbanStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHasGacStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.hasGac.update.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyHasGacStatus === "failed") {
      triggerToastDS(t("companyFollowUp.hasGac.update.failure") || "", "ERROR");
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyHasGacStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyAutoApproveDriverAccessRequestsStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.autoApproveDriverAccessRequests.update.success") ||
          "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
      void dispatch(companyFollowUpAsync());
    } else if (
      updateCompanyAutoApproveDriverAccessRequestsStatus === "failed"
    ) {
      triggerToastDS(
        t("companyFollowUp.autoApproveDriverAccessRequests.update.failure") ||
          "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyAutoApproveDriverAccessRequestsStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHasGeolocationStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.hasGeolocation.update.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyHasGeolocationStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.hasGeolocation.update.failure") || "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyHasGeolocationStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyAnalyticsStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.hasAnalytics.update.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyAnalyticsStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.hasAnalytics.update.failure") || "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyAnalyticsStatus, t, dispatch]);

  useEffect(() => {
    if (deleteCompanyStatus === "success") {
      triggerToastDS(t("companyFollowUp.delete.success") || "", "SUCCESS");
      dispatch(companyFollowUpSlice.actions.resetDeleteCompanyStatus());
      dispatch(companyFollowUpSlice.actions.resetCompanyFollowUpStatus());
    } else if (deleteCompanyStatus === "failed") {
      triggerToastDS(t("companyFollowUp.delete.failure") || "", "ERROR");
      dispatch(companyFollowUpSlice.actions.resetDeleteCompanyStatus());
    }
  }, [deleteCompanyStatus, t, dispatch]);

  useEffect(() => {
    if (companyFollowUpListStatus === "idle") {
      void dispatch(companyFollowUpAsync());
    }
  }, [companyFollowUpListStatus, dispatch]);

  const columnsDisplayed: {
    [index in keyof CompanyFollowUpDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 200,
      },
      balance: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) => {
          return value ? <>{Math.round(value / 100)} €</> : <>N/A</>;
        },
      },
      acronym: {
        type: "string",
        width: 300,
        renderCell: (props) => {
          if (props.value) return <>{props.value}</>;
          return (
            <AcronymInput
              isAcronymValid={isAcronymValid}
              companyUuid={props.row["companyUuid"] as string}
            />
          );
        },
      },
      externalIban: {
        type: "string",
        width: 360,
        renderCell: (props) => {
          if (props.value) return <>{props.value}</>;
          return (
            <ExternalIbanInput
              setIsExternalIbanModalVisible={(
                visible: boolean,
                iban?: string,
              ) =>
                handleModalVisibility(
                  visible,
                  iban,
                  props.row["companyUuid"] as string,
                  props.row["name"] as string,
                )
              }
            />
          );
        },
      },
      hasGac: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <SwitchCellContainer>
              <Switch
                checked={props.row["hasGac"] as boolean}
                size={"S"}
                onClick={() => {
                  void dispatch(
                    updateCompanyHasGacAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasGac: !(props.row["hasGac"] as boolean),
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </SwitchCellContainer>
          );
        },
      },
      hasGeolocation: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <SwitchCellContainer>
              <Switch
                checked={props.row["hasGeolocation"] as boolean}
                size={"S"}
                onClick={() => {
                  void dispatch(
                    updateCompanyHasGeolocationAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasGeolocation: !(props.row["hasGeolocation"] as boolean),
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </SwitchCellContainer>
          );
        },
      },
      autoApproveDriverAccessRequests: {
        type: "string",
        width: 320,
        renderCell: (props) => {
          return (
            <SwitchCellContainer>
              <Switch
                checked={!!props.row["autoApproveDriverAccessRequests"]}
                size={"S"}
                onClick={() =>
                  handleUpdateAutoApproveDriverAccessRequests(
                    props.row["companyUuid"] as string,
                    !props.row["autoApproveDriverAccessRequests"],
                  )
                }
              />
            </SwitchCellContainer>
          );
        },
      },
      hasAnalytics: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <HasAnalyticsCellContainer>
              <Switch
                checked={props.row["hasAnalytics"] as boolean}
                size={"S"}
                disabled={props.row["hasAdvancedAnalytics"] as boolean}
                onClick={() => {
                  void dispatch(
                    updateCompanyAnalyticsAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasAnalytics: !(props.row["hasAnalytics"] as boolean),
                      hasAdvancedAnalytics:
                        props.row["hasAdvancedAnalytics"] as boolean,
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </HasAnalyticsCellContainer>
          );
        },
      },
      hasAdvancedAnalytics: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <HasAdvancedAnalyticsCellContainer>
              <Switch
                checked={props.row["hasAdvancedAnalytics"] as boolean}
                size={"S"}
                disabled={!(props.row["hasAnalytics"] as boolean)}
                onClick={() => {
                  void dispatch(
                    updateCompanyAnalyticsAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasAnalytics: props.row["hasAnalytics"] as boolean,
                      hasAdvancedAnalytics: !(
                        props.row["hasAdvancedAnalytics"] as boolean
                      ),
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </HasAdvancedAnalyticsCellContainer>
          );
        },
      },
      status: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      deleteCompany: {
        type: "string",
        width: 220,
        valueFormatter: (
          value: string | null,
          row: CompanyFollowUpDisplayed & { id: string },
        ) => {
          if (row["status"] === t("companyFollowUp.status.DELETE")) {
            return (
              <TextCapitalized>
                {t("companyFollowUp.status.alreadyDeleted")}
              </TextCapitalized>
            );
          }
          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: <DeleteOutlined />,
                  size: "L",
                }}
                buttonType="danger"
                format={"fill"}
                onClick={() => {
                  if (!authorizedAdmin) {
                    return;
                  }
                  handleDeleteCompanyModalVisibility(
                    true,
                    row["id"],
                    row["name"],
                  );
                }}
                disabled={!authorizedAdmin}
              />
            </StyledColumnButton>
          );
        },
      },
    };
  }, [
    isAcronymValid,
    handleModalVisibility,
    dispatch,
    adminUser?.email,
    authorizedAdmin,
    handleDeleteCompanyModalVisibility,
    handleUpdateAutoApproveDriverAccessRequests,
    t,
  ]);

  useEffect(() => {
    if (companyListWithId && companyFollowUpListStatus === "success") {
      setListDisplayed(companyListWithId);
    }
  }, [companyListWithId, companyFollowUpListStatus]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (
    params.tab !== "companyFollowUp" ||
    adminUser?.email === "exterieur1@qivia.fr"
  ) {
    return;
  }

  return (
    <StyledContainer>
      <Spacer y={1} />
      <a href={CONTRACTS_DRIVE_FOLDER_URL} target="_blank" rel="noreferrer">
        {t("companyFollowUp.contractsDriveLink")}
      </a>
      <Spacer y={1} />
      <ExternalIbanModal
        visible={modalState.isVisible}
        onClose={() => setModalState({ isVisible: false })}
        selectedIban={modalState.iban}
        selectedName={modalState.name}
        onConfirm={handleConfirmExternalIban}
        modalType="ADD"
      />
      <MaterialUiTable<keyof CompanyFollowUpDisplayed, CompanyFollowUpDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
      <DeleteCompanyModal
        visible={deleteCompanyModalState.isVisible}
        onClose={() => setDeleteCompanyModalState({ isVisible: false })}
        selectedCompanyName={deleteCompanyModalState.name}
        onConfirm={handleConfirmDeleteCompany}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SwitchCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const HasAnalyticsCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const HasAdvancedAnalyticsCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
